import { type ConversionEvent, type ConversionSettings, formatBRL, type GoogleConversionAction, META_EVENT_LABELS, type SaveConversionSettings, type SaveGoogleConversions } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, PasswordInput, Select, Spinner, Switch } from '@dnx/ui';
import { useRouterState } from '@tanstack/react-router';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fieldErrors } from '../clinical/patients-page.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

const settingsQuery = queryOptions({ queryKey: ['conversions', 'settings'], queryFn: () => api<ConversionSettings>('/conversions/settings') });
const eventsQuery = queryOptions({ queryKey: ['conversions', 'events'], queryFn: () => api<ConversionEvent[]>('/conversions/events'), refetchInterval: 15_000 });
const PLATFORM: Record<string, string> = { meta: 'Meta', google: 'Google' };
const STATUS: Record<ConversionEvent['status'], [string, 'primary' | 'success' | 'danger']> = { pending: ['Na fila', 'primary'], sent: ['Enviado', 'success'], failed: ['Falhou', 'danger'] };

function MetaCard({ s }: { s: ConversionSettings }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ enabled: s.metaEnabled, dataset: s.metaDatasetId ?? '', page: s.metaPageId ?? '', token: '', test: s.metaTestCode ?? '' });
  useEffect(() => setF((x) => ({ ...x, enabled: s.metaEnabled })), [s.metaEnabled]);
  const save = useMutation({
    mutationFn: () => api<ConversionSettings>('/conversions/settings', {
      method: 'PUT',
      body: { metaEnabled: f.enabled, metaDatasetId: f.dataset.trim() || null, metaPageId: f.page.trim() || null, ...(f.token.trim() ? { metaToken: f.token.trim() } : {}), metaTestCode: f.test.trim() || null } satisfies Partial<SaveConversionSettings>,
    }),
    onSuccess: (n) => { qc.setQueryData(settingsQuery.queryKey, n); setF((x) => ({ ...x, token: '' })); },
  });
  const errs = fieldErrors(save.error);
  return (
    <Card>
      <CardHeader title="Meta (Facebook e Instagram Ads)" description="Leads de anúncios Click-to-WhatsApp voltam para o conjunto de dados (pixel) da clínica: conversa iniciada, avaliação agendada e venda com o valor do orçamento. A Meta usa isso para achar mais pacientes como os que fecharam." />
      <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Switch checked={f.enabled} onChange={(v) => setF({ ...f, enabled: v })} label="Enviar conversões para a Meta" description="Só leads que chegaram por anúncio. Não sai nome, telefone nem e-mail do paciente: só o identificador do clique no anúncio." />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="ID do conjunto de dados (pixel)" hint="Gerenciador de Eventos › Fontes de dados." error={errs.metaDatasetId}><Input value={f.dataset} onChange={(e) => setF({ ...f, dataset: e.target.value })} inputMode="numeric" /></Field>
          <Field label="ID da página do Facebook" hint="A página ligada ao WhatsApp dos anúncios." error={errs.metaPageId}><Input value={f.page} onChange={(e) => setF({ ...f, page: e.target.value })} inputMode="numeric" /></Field>
          <Field label="Token de acesso (Conversions API)" hint={s.metaTokenLast4 ? `Salvo (termina em ${s.metaTokenLast4}). Preencha só para trocar.` : 'Gerenciador de Eventos › Configurações › Gerar token.'} error={errs.metaToken}>
            <PasswordInput value={f.token} onChange={(e) => setF({ ...f, token: e.target.value })} autoComplete="off" />
          </Field>
          <Field label="Código de teste (opcional)" hint="Com ele, os eventos aparecem em Testar eventos e não contam nas campanhas. Apague para valer."><Input value={f.test} onChange={(e) => setF({ ...f, test: e.target.value })} /></Field>
        </div>
        {save.error && !Object.keys(errs).length && <Alert tone="danger">{save.error.message}</Alert>}
        {save.isSuccess && <Alert tone="success">Salvo{f.enabled ? ' e conferido na Meta' : ''}.</Alert>}
        <div><Button type="submit" loading={save.isPending}>Salvar</Button></div>
      </form>
    </Card>
  );
}

const GOOGLE_RESULT: Record<string, ['success' | 'warning' | 'danger', string]> = {
  ok: ['success', 'Conta Google conectada. Escolha a conta de anúncios e as ações de conversão.'],
  cancelado: ['warning', 'A conexão foi cancelada no Google.'], expirado: ['warning', 'O link expirou. Tente de novo.'],
  erro: ['danger', 'Não foi possível conectar a conta Google. Tente de novo.'],
};
const STAGES: [keyof SaveGoogleConversions['actions'], string][] = [['created', 'Lead (formulário enviado)'], ['evaluation_scheduled', 'Avaliação agendada'], ['won', 'Venda (com o valor)']];

function GoogleCard({ s }: { s: ConversionSettings }) {
  const qc = useQueryClient();
  const search = useRouterState({ select: (r) => r.location.search as Record<string, unknown> });
  const result = typeof search.google === 'string' ? GOOGLE_RESULT[search.google] : undefined;
  const g = s.google;
  const [customer, setCustomer] = useState(g.customerId ?? '');
  const [mcc, setMcc] = useState(g.loginCustomerId ?? '');
  const [actions, setActions] = useState(g.actions);
  const [enabled, setEnabled] = useState(g.enabled);
  const [list, setList] = useState<GoogleConversionAction[] | null>(null);
  const connect = useMutation({ mutationFn: () => api<{ url: string }>('/conversions/google/start', { method: 'POST' }), onSuccess: (r) => { window.location.href = r.url; } });
  const load = useMutation({
    mutationFn: () => api<GoogleConversionAction[]>(`/conversions/google/actions?${new URLSearchParams({ cliente: customer, ...(mcc ? { mcc } : {}) })}`),
    onSuccess: setList,
  });
  const save = useMutation({
    mutationFn: () => api<ConversionSettings>('/conversions/google', { method: 'PUT', body: { enabled, customerId: customer || null, loginCustomerId: mcc || null, actions } }),
    onSuccess: (n) => qc.setQueryData(settingsQuery.queryKey, n),
  });
  const disconnect = useMutation({ mutationFn: () => api<ConversionSettings>('/conversions/google/disconnect', { method: 'POST' }), onSuccess: (n) => qc.setQueryData(settingsQuery.queryKey, n) });
  const opts = list ?? Object.values(actions).filter(Boolean).map((r) => ({ resourceName: r!, name: r!.split('/').pop()!, category: null }));
  return (
    <Card>
      <CardHeader title="Google Ads" description="Leads de formulários com clique do Google (gclid) voltam como conversão offline para a conta de anúncios da clínica: lead, avaliação agendada e venda com valor." />
      <div className="grid gap-4 p-5">
        {result && <Alert tone={result[0]}>{result[1]}</Alert>}
        {!g.available ? <p className="text-sm text-fg-muted">A integração com o Google Ads ainda não foi liberada pela plataforma.</p> : !g.connected ? (
          <div><Button onClick={() => connect.mutate()} loading={connect.isPending}>Conectar conta Google</Button>
            <p className="mt-2 text-[12.5px] text-fg-muted">Use uma conta com acesso à conta de anúncios da clínica.</p></div>
        ) : (
          <>
            <p className="flex flex-wrap items-center gap-2 text-sm text-fg">Conectado como <strong>{g.accountEmail ?? 'conta Google'}</strong>
              <Button size="sm" variant="ghost" loading={disconnect.isPending} onClick={() => disconnect.mutate()}>Desconectar</Button></p>
            <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <Field label="ID da conta de anúncios" hint="Ex.: 123-456-7890"><Input value={customer} onChange={(e) => setCustomer(e.target.value)} /></Field>
              <Field label="ID da conta gerente (MCC, opcional)"><Input value={mcc} onChange={(e) => setMcc(e.target.value)} /></Field>
              <Button variant="secondary" disabled={customer.replace(/\D/g, '').length !== 10} loading={load.isPending} onClick={() => load.mutate()}>Buscar ações</Button>
            </div>
            {load.error && <Alert tone="danger">{load.error.message}</Alert>}
            {list && !list.length && <Alert tone="warning">Nenhuma ação do tipo “Importar cliques” ativa nesta conta. Crie em Metas › Conversões › Nova ação › Importar › CRM (cliques).</Alert>}
            <div className="grid gap-3 sm:grid-cols-3">
              {STAGES.map(([k, label]) => (
                <Field key={k} label={label}>
                  <Select value={actions[k] ?? ''} onChange={(e) => setActions({ ...actions, [k]: e.target.value || null })}>
                    <option value="">Não enviar</option>
                    {opts.map((a) => <option key={a.resourceName} value={a.resourceName}>{a.name}</option>)}
                  </Select>
                </Field>
              ))}
            </div>
            <Switch checked={enabled} onChange={setEnabled} label="Enviar conversões para o Google Ads" />
            {save.error && <Alert tone="danger">{save.error.message}</Alert>}
            {save.isSuccess && <Alert tone="success">Salvo e conferido no Google.</Alert>}
            <div><Button onClick={() => save.mutate()} loading={save.isPending}>Salvar</Button></div>
          </>
        )}
      </div>
    </Card>
  );
}

/** Configurações › Conversões: devolve os resultados do funil para as plataformas de anúncio. */
export function ConversionsSettingsPage() {
  const { data } = useQuery(settingsQuery);
  const { data: events } = useQuery(eventsQuery);
  return (
    <div className="grid gap-6">
      <SectionHeader title="Conversões dos anúncios" description="O que acontece depois do clique (agendou, fechou) volta para a plataforma de anúncio, para ela otimizar as campanhas." />
      {data ? <MetaCard s={data} /> : <Spinner className="size-5 text-fg-subtle" label="Carregando" />}
      {data && <GoogleCard s={data} />}
      <Card>
        <CardHeader title="Últimos eventos" />
        <ul className="divide-y divide-border">
          {events?.map((e) => (
            <li key={e.id} className="grid gap-1 px-5 py-3 text-sm sm:grid-cols-[1fr_200px_120px] sm:items-center">
              <span className="min-w-0"><span className="block truncate font-medium text-fg">{e.contactName}</span>
                <span className="text-[12.5px] text-fg-muted">{new Date(e.occurredAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}{e.lastError && e.status !== 'sent' ? ` · ${e.lastError}` : ''}</span></span>
              <span className="text-fg-muted">{PLATFORM[e.platform]} · {META_EVENT_LABELS[e.eventName] ?? e.eventName}{e.valueCents != null ? ` · ${formatBRL(e.valueCents)}` : ''}</span>
              <span className="flex gap-1.5 sm:justify-end"><Badge tone={STATUS[e.status][1]}>{STATUS[e.status][0]}</Badge>{e.test && <Badge tone="warning">Teste</Badge>}</span>
            </li>
          ))}
          {events && !events.length && <li className="px-5 py-6 text-sm text-fg-muted">Nenhum evento ainda. Eles aparecem aqui quando um lead de anúncio avança no funil.</li>}
        </ul>
      </Card>
    </div>
  );
}
